
import React from 'react';
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
  DrawerDescription,
  DrawerFooter,
} from '@/components/ui/drawer';
import { ModalCampaign, ModalCampaignQuestion } from '@/types/modalCampaigns';
import ModalContentRenderer from './ModalContentRenderer';
import ModalFooter from './ModalFooter';

interface ModalMobileVariantProps {
  campaign: ModalCampaign;
  questions: ModalCampaignQuestion[];
  open: boolean;
  userAnswers: Record<string, string[]>;
  quizSubmitted: boolean;
  quizPassed: boolean;
  score: number;
  attemptNumber: number;
  currentQuestionIndex: number;
  canDismiss: boolean;
  onOpenChange: (open: boolean) => void;
  onDismiss: (permanently?: boolean) => void;
  onAnswer: (questionId: string, selectedOptions: string[]) => void;
  onSubmit: () => void;
  onReset: () => void;
  onPrevQuestion: () => void;
  onNextQuestion: () => void;
}

const ModalMobileVariant: React.FC<ModalMobileVariantProps> = ({
  campaign,
  questions,
  open,
  userAnswers,
  quizSubmitted,
  quizPassed,
  score,
  attemptNumber,
  currentQuestionIndex,
  canDismiss,
  onOpenChange,
  onDismiss,
  onAnswer,
  onSubmit,
  onReset,
  onPrevQuestion,
  onNextQuestion
}) => {
  const [dismissPermanently, setDismissPermanently] = React.useState(false);
  
  const handleDismiss = () => {
    if (canDismiss) {
      onDismiss(dismissPermanently);
    }
  };

  return (
    <Drawer open={open} onOpenChange={onOpenChange}>
      <DrawerContent className="max-h-[90vh] px-0 pb-0 pt-0">
        <DrawerHeader className="px-4">
          <DrawerTitle>{campaign.title}</DrawerTitle>
          {campaign.has_quiz && <DrawerDescription>Deze Nieuws+ bevat een quiz</DrawerDescription>}
        </DrawerHeader>
        
        <div className="flex flex-col h-full overflow-hidden">
          <div className="px-4 flex-grow overflow-y-auto pb-4">
            <ModalContentRenderer
              content={campaign.content}
              hasQuiz={campaign.has_quiz}
              questions={questions}
              userAnswers={userAnswers}
              quizSubmitted={quizSubmitted}
              quizPassed={quizPassed}
              score={score}
              minScore={campaign.min_score !== null ? campaign.min_score : questions.reduce((sum, q) => sum + q.points, 0)}
              attemptNumber={attemptNumber}
              maxAttempts={campaign.max_attempts}
              attemptsExhausted={campaign.max_attempts !== null && attemptNumber >= campaign.max_attempts}
              currentQuestionIndex={currentQuestionIndex}
              onAnswer={onAnswer}
              onSubmit={onSubmit}
              onReset={onReset}
              onPrevQuestion={onPrevQuestion}
              onNextQuestion={onNextQuestion}
            />
          </div>
          
          <DrawerFooter className="px-4 border-t">
            <ModalFooter
              campaign={campaign}
              canDismiss={canDismiss}
              dismissPermanently={dismissPermanently}
              onDismissPermanentlyChange={setDismissPermanently}
              onDismiss={handleDismiss}
              isMobile={true}
            />
          </DrawerFooter>
        </div>
      </DrawerContent>
    </Drawer>
  );
};

export default ModalMobileVariant;
